import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import type { TransactionDetailsData } from "../../types/transactions";

type StoredToken = { AccessToken?: string };

export type GetUserTransactionDetailsParams = {
  userId: string;
  transactionId: string;
  detailsRoute?: string;
};

/** First segment of the `queryKey`; export so consumers can target invalidations. */
export const GET_USER_TRANSACTION_DETAILS_QUERY_KEY =
  "GET_USER_TRANSACTION_DETAILS";

export function buildUserTransactionDetailsPath(
  userId: string,
  transactionId: string,
  detailsRoute: string = API_ROUTES.GET_USER_TRANSACTION_DETAILS_V1,
): string {
  return detailsRoute
    .replace("{user_id}", encodeURIComponent(userId))
    .replace("{transaction_id}", encodeURIComponent(transactionId));
}

export async function fetchUserTransactionDetails(
  params: GetUserTransactionDetailsParams,
): Promise<TransactionDetailsData> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const response = await instance.get(
    buildUserTransactionDetailsPath(
      params.userId,
      params.transactionId,
      params.detailsRoute,
    ),
  );

  const body = response?.data?.data ?? response?.data ?? null;
  if (!body || typeof body !== "object") {
    throw new Error("Invalid transaction details response");
  }

  return body as TransactionDetailsData;
}

export type UseGetUserTransactionDetailsOptions = {
  userId?: string | null;
  transactionId?: string | null;
  enabled?: boolean;
  detailsRoute?: string;
};

export function useGetUserTransactionDetails({
  userId,
  transactionId,
  enabled = true,
  detailsRoute = API_ROUTES.GET_USER_TRANSACTION_DETAILS_V1,
}: UseGetUserTransactionDetailsOptions): UseQueryResult<
  TransactionDetailsData,
  Error
> {
  const resolvedUserId = userId?.trim() ?? "";
  const resolvedTransactionId = transactionId?.trim() ?? "";

  return useQuery<TransactionDetailsData, Error>({
    queryKey: [
      GET_USER_TRANSACTION_DETAILS_QUERY_KEY,
      detailsRoute,
      resolvedUserId,
      resolvedTransactionId,
    ],
    queryFn: () =>
      fetchUserTransactionDetails({
        userId: resolvedUserId,
        transactionId: resolvedTransactionId,
        detailsRoute,
      }),
    enabled: Boolean(enabled && resolvedUserId && resolvedTransactionId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export default useGetUserTransactionDetails;
